import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
  customerAddProductReturn,
  getReturnProducts,
  getBillForReturn,
} from "../redux/slices/inventorySlice";

const ManageCustomerReturn = () => {
  const dispatch = useDispatch();
  const { returns, pagination, loading, billData } = useSelector(
    (state) => state.inventory
  );

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [returnQty, setReturnQty] = useState("");
  const [reason, setReason] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  /* ===============================
     FETCH RETURNS LIST
  ================================ */
  useEffect(() => {
    dispatch(getReturnProducts({ page, search }));
  }, [dispatch, page, search]);

  /* ===============================
     SEARCH BILL BY INVOICE
  ================================ */
  const handleInvoiceSearch = async () => {
    if (!invoiceNumber) return;

    try {
      await dispatch(getBillForReturn(invoiceNumber)).unwrap();
    } catch (err) {
      Swal.fire("Error", err, "error");
    }
  };

  /* ===============================
     SUBMIT RETURN
  ================================ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedItem) {
      return Swal.fire("Select Product", "Please select a product", "warning");
    }

    if (returnQty <= 0 || returnQty > selectedItem.soldQty) {
      return Swal.fire(
        "Invalid Quantity",
        "Return quantity is invalid",
        "warning"
      );
    }

    try {
      await dispatch(
        customerAddProductReturn({
          billId: billData.billId,
          productId: selectedItem.productId,
          quantity: returnQty,
          reason,
        })
      ).unwrap();

      Swal.fire("Success", "Return added successfully", "success");

      // reset
      setInvoiceNumber("");
      setSelectedItem(null);
      setReturnQty("");
      setReason("");

      dispatch(getReturnProducts({ page: 1, search }));
      document.getElementById("closeReturnModal").click();
    } catch (err) {
      Swal.fire("Error", err, "error");
    }
  };

  return (
    <div className="container mt-4">
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between">
          <h6>Manage Customer Returns</h6>
          <button
            className="btn btn-sm btn-primary"
            data-bs-toggle="modal"
            data-bs-target="#returnModal"
          >
            + Add Return
          </button>
        </div>

        <div className="card-body">
          {/* SEARCH */}
          <input
            className="form-control form-control-sm mb-3"
            placeholder="Search by reason..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Reason</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {returns?.length ? (
                  returns.map((r, i) => (
                    <tr key={r._id}>
                      <td>{i + 1}</td>
                      <td>{r.bill?.invoiceNumber}</td>
                      <td>{r.bill?.customerName}</td>
                      <td>{r.product?.name}</td>
                      <td>{r.quantity}</td>
                      <td>{r.reason}</td>
                      <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      <div className="modal fade" id="returnModal">
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h6>Add Customer Return</h6>
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  id="closeReturnModal"
                />
              </div>

              <div className="modal-body">
                {/* Invoice */}
                <label className="form-label">Invoice Number</label>
                <div className="d-flex gap-2 mb-2">
                  <input
                    className="form-control form-control-sm"
                    value={invoiceNumber}
                    placeholder="INV-000000"
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={handleInvoiceSearch}
                  >
                    Search
                  </button>
                </div>

                {/* Products */}
                {billData?.items?.length > 0 && (
                  <>
                    <label className="form-label">Select Product</label>
                    <select
                      className="form-select form-select-sm mb-2"
                      onChange={(e) =>
                        setSelectedItem(
                          billData.items.find(
                            (i) => i.productId === e.target.value
                          )
                        )
                      }
                    >
                      <option value="">Select</option>
                      {billData.items.map((i) => (
                        <option key={i.productId} value={i.productId}>
                          {i.name} (Sold: {i.soldQty})
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {/* Quantity */}
                {selectedItem && (
                  <>
                    <label className="form-label">Return Quantity</label>
                    <input
                      type="number"
                      className="form-control form-control-sm mb-2"
                      max={selectedItem.soldQty}
                      value={returnQty}
                      onChange={(e) => setReturnQty(e.target.value)}
                    />
                  </>
                )}

                <label className="form-label">Reason</label>
                <textarea
                  className="form-control form-control-sm"
                  rows="2"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button className="btn btn-sm btn-secondary" data-bs-dismiss="modal">
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCustomerReturn;
